/**
 * useAutoSave Hook
 * Provides debounced auto-save functionality for BRR form data
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { BRRApiClient } from '../services';
import { BRRData } from '../services/types';

interface UseAutoSaveOptions {
  batchId: string;
  formData: BRRData;
  apiClient: BRRApiClient;
  debounceMs?: number;
  enabled?: boolean;
  onSaved?: () => void;
  onError?: (error: Error) => void;
}

interface UseAutoSaveReturn {
  isSaving: boolean;
  lastSaved: Date | null;
  error: Error | null;
  saveNow: () => Promise<void>;
}

/**
 * Hook that automatically saves form data after a debounce period
 *
 * @example
 * const { isSaving, lastSaved, saveNow } = useAutoSave({
 *   batchId: 'B-2026-0147',
 *   formData: currentFormData,
 *   apiClient: brrApi,
 *   debounceMs: 2000,
 *   onSaved: () => {},
 *   onError: (err) => console.error('Save failed:', err)
 * });
 */
export const useAutoSave = ({
  batchId,
  formData,
  apiClient,
  debounceMs = 2000,
  enabled = true,
  onSaved,
  onError
}: UseAutoSaveOptions): UseAutoSaveReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const timeoutRef = useRef<number | null>(null);
  const isSavingRef = useRef(false);
  const formDataRef = useRef(formData);

  // Update ref when formData changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Save function
  const save = useCallback(async () => {
    if (isSavingRef.current || !enabled) return;

    isSavingRef.current = true;
    setIsSaving(true);
    setError(null);

    try {
      await apiClient.saveDraft(batchId, formDataRef.current);
      const now = new Date();
      setLastSaved(now);
      onSaved?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      onError?.(error);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [batchId, apiClient, enabled, onSaved, onError]);

  // Manual save function (exposed to caller)
  const saveNow = useCallback(async () => {
    // Clear any pending auto-save
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await save();
  }, [save]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = window.setTimeout(() => {
      void save();
    }, debounceMs);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData, enabled, debounceMs, save]);

  return {
    isSaving,
    lastSaved,
    error,
    saveNow
  };
};
